import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { api, ApiError } from '@/api/client';
import { setLocale } from '@/i18n';
import type { Locale } from '@/i18n/locale';
import { chooseMode } from '@/theme';
import type { ThemeMode } from '@/theme/mode';
import { useGamesStore } from '@/stores/games';
import type { Me, Subject, User } from '@/api/types';

/**
 * Who the current visitor is. Loaded once on app start.
 *
 * The session itself lives in an HttpOnly cookie the SPA cannot read, so this store holds only
 * what the server told us about it — there is no token here to leak.
 */
export const useSessionStore = defineStore('session', () => {
  const subject = ref<Subject | null>(null);
  const user = ref<User | null>(null);
  const loading = ref(false);
  /** Distinguishes "not loaded yet" from "loaded, nobody signed in". */
  const loaded = ref(false);

  const isSignedIn = computed(() => subject.value !== null);
  /**
   * Hosting needs a verified email; playing does not.
   *
   * Presence, not `!== null`: an absent `user` is not a host either, and a /me answered by
   * something that is not our API — a proxy error page, a captive portal — parses into an object
   * with no `user` at all. `!== null` said yes to that.
   */
  const canHost = computed(() => Boolean(user.value));

  /** Whether this person has ever chosen a name, as opposed to being shown a fallback. */
  const hasChosenName = computed(
    () => subject.value?.displayName !== null && subject.value?.displayName !== undefined,
  );

  /**
   * The name to put on screen.
   *
   * The backend used to *store* a name derived from the email's local part, which is how
   * "armand.maree" ended up in the header with no way to change it. It is a display fallback
   * now and nothing more: the stored name stays null until somebody chooses one, and nothing
   * here ever sends this guess back to the API. See docs/ROADMAP.md, Phase 7.
   */
  const displayName = computed(
    () => subject.value?.displayName ?? user.value?.email?.split('@')[0] ?? '',
  );

  /**
   * The /me in flight, if there is one.
   *
   * Module-scope rather than a ref: nothing renders it, and a `ref` holding a promise is a
   * reactive dependency waiting to be depended on by accident.
   */
  let inFlight: Promise<void> | null = null;

  /**
   * Resolves who this is, once per visit.
   *
   * **Idempotent on purpose.** The router guard used to ask `loaded` before calling this, which
   * made the guard the only thing keeping it to one call — and the guard no longer waits for the
   * answer on a route with no gate to apply, so `loaded` is now false for a window somebody can
   * navigate inside. Both the "already answered" and the "still asking" cases belong here, where
   * every caller gets them.
   */
  async function load(): Promise<void> {
    if (loaded.value) return;

    inFlight ??= resolve().finally(() => {
      inFlight = null;
    });

    return inFlight;
  }

  async function resolve(): Promise<void> {
    loading.value = true;
    try {
      const me = await api.get<Me>('/me');
      subject.value = me.subject;
      // Normalised on the way in, so nothing downstream has to handle both null and undefined.
      user.value = me.user ?? null;

      /*
       * A language chosen on another device wins over whatever this browser detected, and is
       * mirrored locally so the *next* visit paints in it rather than flipping once this call
       * lands. Null means never chosen, and detection keeps the screen it already has.
       */
      if (me.subject.locale) await setLocale(me.subject.locale);

      /*
       * And the appearance, on the same terms — mirrored into this browser so the *next* visit
       * paints in it rather than flipping once this call lands.
       *
       * Only a non-null value overrides, and that is not symmetry for its own sake. Null on the
       * profile means both "never chose" and "chose to follow this device", because un-choosing
       * removes the attribute — so it cannot say which, and acting on it would throw away a
       * choice somebody made in this browser before they ever had a session.
       */
      if (me.subject.theme) chooseMode(me.subject.theme);
    } catch (error) {
      // A 401 is the normal state for a first-time visitor, not an error to surface.
      if (error instanceof ApiError && error.isNotAuthenticated) {
        subject.value = null;
        user.value = null;
      } else {
        throw error;
      }
    } finally {
      loading.value = false;
      loaded.value = true;
    }
  }

  async function continueAsGuest(displayName: string): Promise<void> {
    const result = await api.post<{ subject: Subject }>('/auth/guest', { displayName });
    subject.value = result.subject;
    user.value = null;
  }

  /**
   * Applies a language and, for anybody with a subject, saves it to the profile.
   *
   * The order matters: the page changes first, so somebody with no session — most players —
   * gets the whole feature, and a failed save leaves the screen in the language they asked
   * for rather than snapping back. Rethrown so the screen can say it will not follow them to
   * their next device.
   */
  async function chooseLocale(locale: Locale): Promise<void> {
    await setLocale(locale);

    if (!subject.value) return;

    const result = await api.patch<{ subject: Subject }>('/me', { locale });
    subject.value = result.subject;
  }

  /**
   * Applies an appearance and, for anybody with a subject, saves it to the profile.
   *
   * Same order and the same reasoning as `chooseLocale`: the page changes first, so somebody
   * with no session — most players — gets the whole feature, and a failed save leaves the screen
   * looking the way they asked rather than snapping back. Rethrown so the screen can say it will
   * not follow them to their next device.
   *
   * **`null` is sent, not omitted.** It means "go back to following this device", and a PATCH
   * that left the field out would mean "leave the appearance alone" — so the one thing somebody
   * cannot do is undo.
   */
  async function chooseThemeMode(mode: ThemeMode | null): Promise<void> {
    chooseMode(mode);

    if (!subject.value) return;

    const result = await api.patch<{ subject: Subject }>('/me', { theme: mode });
    subject.value = result.subject;
  }

  /**
   * The one call that sets a name deliberately. Deliberately does not catch: a name that did
   * not save must not look like one that did, and only the screen can say so.
   */
  async function updateDisplayName(newName: string): Promise<void> {
    const result = await api.patch<{ subject: Subject }>('/me', { displayName: newName });
    subject.value = result.subject;
  }

  /**
   * Asks for a code. Always resolves on the backend's side regardless of whether the address
   * has an account, so nothing here can be used to enumerate users.
   */
  async function requestSignInCode(email: string): Promise<void> {
    await api.post('/auth/magic-link/request', { email });
  }

  /**
   * The backend sets the session cookie; this only records what came back.
   *
   * Deliberately does not catch: INVALID_CODE, CODE_EXPIRED, TOO_MANY_ATTEMPTS and RATE_LIMITED
   * each need a different response from the view, and a store that swallowed them would leave
   * the sign-in screen unable to tell "wrong code" from "start over".
   */
  async function verifySignInCode(email: string, code: string): Promise<void> {
    const result = await api.post<{ subject: Subject; user: User }>('/auth/magic-link/verify', {
      email,
      code,
    });
    subject.value = result.subject;
    user.value = result.user;
    // Marked loaded, or the route guard would resolve a session it was just handed.
    loaded.value = true;
  }

  async function signOut(): Promise<void> {
    await api.post('/auth/logout');
    subject.value = null;
    user.value = null;
    // The games store holds what this browser was looking at. Sign-out means forget it — a
    // shared laptop must not hand the next person somebody else's pools.
    useGamesStore().clear();
  }

  return {
    subject,
    user,
    loading,
    loaded,
    isSignedIn,
    canHost,
    displayName,
    hasChosenName,
    load,
    chooseLocale,
    chooseThemeMode,
    updateDisplayName,
    continueAsGuest,
    requestSignInCode,
    verifySignInCode,
    signOut,
  };
});
